import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./home.css";

// ===== IMAGES =====
import bangles from "../image/bangles.jpg";
import bracelets from "../image/bracelets.jpg";
import chains from "../image/chains.jpg";
import earrings from "../image/earrings.jpg";
import necklaces from "../image/necklaces.jpg";
import rings from "../image/rings.jpg";

import home1 from "../image/home1.jpg";
import home2 from "../image/home2.jpg";
import home3 from "../image/home3.jpg";
import home4 from "../image/home4.jpg";
import home5 from "../image/home5.jpg";
import home6 from "../image/home6.jpg";
import home7 from "../image/home7.jpg";
import home8 from "../image/home8.jpg";
import home9 from "../image/home9.jpg";
import home10 from "../image/home10.jpg";
import home11 from "../image/home11.jpg";
import home12 from "../image/home12.jpg";
import home13 from "../image/home13.jpg";
import home14 from "../image/home14.jpg";
import home15 from "../image/home15.jpg";

// ===== Small SVGs =====
const SearchIcon = ({ className }) => (
  <svg
    className={className}
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.5 18a7.5 7.5 0 1 0 0-15a7.5 7.5 0 0 0 0 15Zm0 0l7 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ===== Aurora Logo (SVG) ===== */
const AuroraLogo = ({ size = 28 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFD369" />
        <stop offset="100%" stopColor="#FCB045" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="30" fill="url(#gold)" opacity="0.08" />
    <path
      d="M32 10L44 36L32 54L20 36L32 10Z"
      stroke="url(#gold)"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="32" cy="32" r="4" fill="url(#gold)" />
  </svg>
);

const formatPrice = (amount, currency) =>
  new Intl.NumberFormat("en-LK", { style: "currency", currency }).format(
    amount
  );

const i18n = (lang) => ({
  tagline: lang === "TA" ? "✨ உள் ஒளியை கண்டறியுங்கள்" : "✨ Discover the Sparkle Within",
  titleA: lang === "TA" ? "நிறைவற்ற" : "Timeless",
  titleB: lang === "TA" ? "அழகு" : "Elegance",
  titleC: lang === "TA" ? "இணைகிறது" : "Meets",
  titleD: lang === "TA" ? "இன்றைய" : "Modern",
  titleE: lang === "TA" ? "செல்வம்" : "Luxury",
  sub:
    lang === "TA"
      ? "ஒவ்வொரு தருணத்தையும் பிரகாசப்படுத்த கைவினை நயம்."
      : "Where passion meets perfection — handcrafted to illuminate every moment.",
  shopNow: lang === "TA" ? "இப்போது வாங்க" : "Shop Now",
  ourStory: lang === "TA" ? "எங்கள் கதை" : "Our Story",
  bestSellers: lang === "TA" ? "✨ அதிகமாக விற்பனையானவை" : "✨ Best Sellers",
  categories: lang === "TA" ? "💍 பிரிவுகளால் வாங்க" : "💍 Shop by Category",
  searchPH:
    lang === "TA" ? "தேடுங்கள்: மோதிரம், வளையல்…" : "Search: ring, bangle…",
  newsletterH: lang === "TA" ? "செய்திமடல்" : "Newsletter",
  newsletterP:
    lang === "TA"
      ? "புதிய வடிவங்கள், சலுகைகள் – உங்கள் மின்னஞ்சலுக்கு."
      : "New designs & offers straight to your inbox.",
  subscribe: lang === "TA" ? "சந்தா" : "Subscribe",
  contact: lang === "TA" ? "தொடர்பு" : "Contact",
  name: lang === "TA" ? "பெயர்" : "Name",
  email: lang === "TA" ? "மின்னஞ்சல்" : "Email",
  message: lang === "TA" ? "செய்தி" : "Message",
  send: lang === "TA" ? "அனுப்பு" : "Send",
  quickView: lang === "TA" ? "விரைவு பார்வை" : "Quick View",
  addToCart: lang === "TA" ? "தேக்குக் கூளில் சேர்" : "Add to Cart",
  wishlist: lang === "TA" ? "பிடித்தவை" : "Wishlist",
  cart: lang === "TA" ? "கூடை" : "Cart",
  total: lang === "TA" ? "மொத்தம்" : "Total",
  emptyCart: lang === "TA" ? "உங்கள் கூடை காலியாக உள்ளது." : "Your cart is empty.",
  noItems: lang === "TA" ? "இன்னும் பொருட்கள் இல்லை." : "No items yet.",
  remove: lang === "TA" ? "நீக்கு" : "Remove",
  certified: lang === "TA" ? "அங்கீகரிக்கப்பட்ட தங்கம்" : "Certified Gold",
  delivery: lang === "TA" ? "நாடு முழுவதும் டெலிவரி" : "Island-wide Delivery",
  payments: lang === "TA" ? "பல கட்டண முறைகள்" : "Multiple Payments",
  addressTA: "528, காலி சாலை, கொழும்பு 03",
  addressEN: "528, Galle Road, Colombo 03",
  estTA: "1962 முதல் — என்றும் உங்களுடன்",
  estEN: "Est. 1962 — With you forever",
  noResults: lang === "TA" ? "பொருத்தமான பெறுபேறுகள் இல்லை." : "No matching items.",
  resultsFor: lang === "TA" ? "தேடல்:" : "Results for:",
  clear: lang === "TA" ? "அழி" : "Clear",
  qty: lang === "TA" ? "அளவு" : "Quantity", // NEW
});

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null); // for chips + modal
  const [showTop, setShowTop] = useState(false);
  const [cart, setCart] = useState([]); // [{...product, qty}]
  const [wishlist, setWishlist] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lang, setLang] = useState("EN");
  const [currency, setCurrency] = useState("LKR");
  const [activeProduct, setActiveProduct] = useState(null);
  const [flashSearch, setFlashSearch] = useState(false);
  const [selectedQty, setSelectedQty] = useState(1); // NEW: for Quick View

  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const searchRef = useRef(null);

  const strings = i18n(lang);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setShowTop(y > 350);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Global hotkeys: Ctrl/Cmd+K or / to focus search
  useEffect(() => {
    const onKey = (e) => {
      const key = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (key === "/" && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Reset selected qty when opening a product
  useEffect(() => {
    if (activeProduct) setSelectedQty(1);
  }, [activeProduct]);

  const toggleMenu = () => setMenuOpen((m) => !m);
  const closeMenu = () => setMenuOpen(false);

  // ===== Canvas sparkles =====
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const setSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    const PCOUNT = 70;
    particlesRef.current = Array.from({ length: PCOUNT }).map(() => ({
      x: Math.random() * canvas.clientWidth,
      y: Math.random() * canvas.clientHeight,
      r: Math.random() * 2 + 0.7,
      vy: Math.random() * 0.6 + 0.25,
      vx: (Math.random() - 0.5) * 0.2,
      a: Math.random() * Math.PI * 2,
      drift: Math.random() * 0.6 + 0.4,
    }));

    const draw = () => {
      const { clientWidth: w, clientHeight: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      const grd = ctx.createRadialGradient(
        w / 2,
        h * 0.55,
        10,
        w / 2,
        h * 0.55,
        Math.max(w, h)
      );
      grd.addColorStop(0, "rgba(255, 215, 0, 0.12)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, w, h);
      for (const p of particlesRef.current) {
        p.a += 0.01;
        p.x += p.vx + Math.sin(p.a) * 0.15 * p.drift;
        p.y += p.vy;
        if (p.y > h + 10) p.y = -10;
        if (p.x > w + 10) p.x = -10;
        if (p.x < -10) p.x = w + 10;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 215, 0, 0.12)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 215, 0, 0.85)";
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => setSize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // ===== Data =====
  const categoryData = {
    Bangles: [
      { id: "b1", img: bangles, name: "Floral Gold Bangle", desc: "22k handcrafted floral design.", price: 285000 },
      { id: "b2", img: home9, name: "Royal Twist Bangle", desc: "Intricate brilliance in every curve.", price: 349000 },
    ],
    Bracelets: [
      { id: "br1", img: bracelets, name: "Diamond Bracelet", desc: "Shine with minimal luxury.", price: 510000 },
      { id: "br2", img: home5, name: "Rose Gold Bracelet", desc: "Modern touch with a golden heart.", price: 295000 },
    ],
    Chains: [
      { id: "c1", img: chains, name: "Classic Gold Chain", desc: "Everyday elegance in pure gold.", price: 225000 },
      { id: "c2", img: home7, name: "Cuban Link Chain", desc: "Bold and confident craftsmanship.", price: 440000 },
    ],
    Earrings: [
      { id: "e1", img: earrings, name: "Ruby Drop Earrings", desc: "Grace and radiance in red.", price: 199000 },
      { id: "e2", img: home10, name: "Diamond Studs", desc: "Timeless sparkle for every moment.", price: 320000 },
    ],
    Necklaces: [
      { id: "n1", img: necklaces, name: "Emerald Gold Necklace", desc: "Green gem of royal beauty.", price: 690000 },
      { id: "n2", img: home13, name: "Pearl Necklace", desc: "Classic, elegant, and forever.", price: 370000 },
    ],
    Rings: [
      { id: "r1", img: rings, name: "Solitaire Ring", desc: "The promise of eternal love.", price: 450000 },
      { id: "r2", img: home12, name: "Designer Ring", desc: "Crafted for the modern soul.", price: 280000 },
    ],
  };

  const allProducts = useMemo(() => Object.values(categoryData).flat(), []);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allProducts.slice(0, 8);
    return allProducts.filter((p) => [p.name, p.desc].some((v) => v.toLowerCase().includes(q)));
  }, [debouncedSearch, allProducts]);

  // === Currency helpers ===
  const convert = (lkrAmount) => {
    if (currency === "LKR") return lkrAmount;
    // USD rough conversion
    return Math.round(lkrAmount / 315);
  };
  const fmt = (lkrAmount) => formatPrice(convert(lkrAmount), currency);

  // === Cart helpers (with quantity) ===
  const addToCart = (p, qty = 1) =>
    setCart((c) => {
      const exist = c.find((i) => i.id === p.id);
      if (exist) {
        return c.map((i) =>
          i.id === p.id ? { ...i, qty: Math.min(99, (i.qty || 1) + qty) } : i
        );
      }
      return [...c, { ...p, qty: Math.max(1, Math.min(99, qty)) }];
    });

  const addToWish = (p) =>
    setWishlist((c) => (c.find((i) => i.id === p.id) ? c : [...c, p]));

  const removeFromCart = (id) => setCart((c) => c.filter((i) => i.id !== id));
  const removeFromWish = (id) => setWishlist((c) => c.filter((i) => i.id !== id));

  const incrementQty = (id) =>
    setCart((c) =>
      c.map((i) => (i.id === id ? { ...i, qty: Math.min(99, (i.qty || 1) + 1) } : i))
    );

  const decrementQty = (id) =>
    setCart((c) =>
      c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, (i.qty || 1) - 1) } : i))
    );

  const setQty = (id, qty) =>
    setCart((c) =>
      c.map((i) =>
        i.id === id ? { ...i, qty: Math.max(1, Math.min(99, qty || 1)) } : i
      )
    );

  const lineTotal = (p) => p.price * (p.qty || 1);
  const cartTotalLKR = cart.reduce((s, p) => s + lineTotal(p), 0);
  const totalItems = cart.reduce((s, p) => s + (p.qty || 1), 0);

  const price = (p) => fmt(p.price);

  const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8 } } };

  // Scroll to search results
  const goToSearchResults = () => {
    const el = document.getElementById("categories");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setFlashSearch(true);
      setTimeout(() => setFlashSearch(false), 900);
    }
    closeMenu();
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") goToSearchResults();
  };

  const onSearchClick = () => {
    if (!debouncedSearch.trim()) {
      searchRef.current?.focus();
    }
    goToSearchResults();
  };

  // keyboard access for chips
  const onCatKeyDown = (e, cat) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveCategory(cat);
    }
  };

  const categories = Object.keys(categoryData);

  return (
    <main className="home">
      {/* ===== SEO Schema ===== */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JewelryStore",
            name: "Royal Crown Jewels",
            address: { "@type": "PostalAddress", streetAddress: "528, Galle Road", addressLocality: "Colombo", addressRegion: "Western", addressCountry: "LK" },
            telephone: "+94764322448",
            url: "https://RoyalCrownjewellers.com",
            image: [home8],
            openingHours: ["Mo-Su 09:00-19:30"],
            priceRange: "$$$",
          }),
        }}
      />

      {/* ===== TOP BAR ===== */}
      <div className="topbar">
        <div className="tb-left">
          <a href="tel:+94764322448">📞 +94 76 432 2448</a>
          <a href="mailto:info@RoyalCrownjewellers.com">✉️ info@RoyalCrownjewellers.com</a>
          <a href="#map">📍 Colombo 03</a>
        </div>
        <div className="tb-right">
          <div className="select-wrap">
            🌐
            <select value={lang} onChange={(e) => setLang(e.target.value)}>
              <option value="EN">EN</option>
              <option value="TA">TA</option>
            </select>
          </div>
          <div className="select-wrap">
            💱
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option value="LKR">LKR</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
      </div>

      {/* ===== HEADER ===== */}
      <header className={`header ${scrolled ? "scrolled" : ""}`}>
        <nav className="navbar container">
          <div className="logo">
            <AuroraLogo size={28} />
            <span>Royal Crown </span>
          </div>

          <ul className={`nav-menu ${menuOpen ? "open" : ""}`}>
            {["Home", "About", "Collections", "Categories", "Contact"].map((item, i) => (
              <li key={i}>
                <a href={`#${item.toLowerCase()}`} onClick={closeMenu}>{item}</a>
              </li>
            ))}
          </ul>

          <div className="nav-cta">
            {/* Polished Search */}
            <div className="search" role="search">
              <span className="search-ic" aria-hidden="true">
                <SearchIcon />
              </span>

              <input
                ref={searchRef}
                id="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={strings.searchPH}
                aria-label="Search products"
                autoComplete="off"
              />

              {search && (
                <button className="clear-ic-btn" onClick={() => setSearch("")} aria-label={strings.clear} title={strings.clear}>×</button>
              )}

              <button className={`go-ic-btn ${debouncedSearch.trim() ? "active" : ""}`} onClick={onSearchClick} aria-label="Search" title="Search">
                <SearchIcon />
              </button>
            </div>

            <button title={strings.wishlist} className="icon-btn" onClick={() => setActiveCategory("Wishlist")}>
              ♡{wishlist.length > 0 && <span className="badge">{wishlist.length}</span>}
            </button>

            <button title={strings.cart} className="icon-btn" onClick={() => setActiveCategory("Cart")}>
              🛍️{totalItems > 0 && <span className="badge">{totalItems}</span>}
            </button>

            <div className={`hamburger ${menuOpen ? "active" : ""}`} onClick={toggleMenu} aria-label="Toggle menu">
              <span></span><span></span><span></span>
            </div>
          </div>
        </nav>
      </header>

      {/* ===== HERO ===== */}
      <section id="home" className="hero" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.75)), url(${home8})` }}>
        <div className="hero-overlay" aria-hidden="true" />
        <canvas ref={canvasRef} className="sparkle-canvas" aria-hidden="true" />

        <motion.div className="hero-content" initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: "easeOut" }}>
          <motion.span className="tagline" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.7 }}>
            {strings.tagline}
          </motion.span>

          <h1 className="hero-title shimmer">
            {strings.titleA} <span>{strings.titleB}</span> {strings.titleC}
            <br />
            {strings.titleD} <span>{strings.titleE}</span>
          </h1>

          <motion.p className="hero-sub" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}>
            {strings.sub}
          </motion.p>

          <motion.div className="cta-buttons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
            <a href="#collections" className="btn primary">{strings.shopNow}</a>
            <a href="#about" className="btn secondary">{strings.ourStory}</a>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== BADGES (new card style) ===== */}
      <section className="badges container">
        <div className="badge-card">
          <div className="badge-ic" aria-hidden="true">✅</div>
          <div>
            <div className="badge-title">{strings.certified}</div>
            <div className="badge-sub">22k/18k • BIS/ISO</div>
          </div>
        </div>

        <div className="badge-card">
          <div className="badge-ic" aria-hidden="true">🚚</div>
          <div>
            <div className="badge-title">{strings.delivery}</div>
            <div className="badge-sub">1–3 days Colombo</div>
          </div>
        </div>

        <div className="badge-card">
          <div className="badge-ic" aria-hidden="true">💳</div>
          <div>
            <div className="badge-title">{strings.payments}</div>
            <div className="badge-sub">Card • Cash • Bank</div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <motion.section id="about" className="about-section container" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>
        <motion.div className="about-image" style={{ backgroundImage: `url(${home1})` }} whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 220, damping: 18 }} />
        <div className="about-text">
          <h2 className="section-title">Our Legacy of Brilliance</h2>
          <p>Since 1962, <b>Royal Crown Jewels</b> has symbolized artistry and devotion. Each creation represents beauty, precision, and the golden touch of tradition. Our heritage continues to inspire timeless elegance across generations.</p>
          <a href="#collections" className="btn primary small">Explore More</a>
        </div>
      </motion.section>

      {/* ===== BEST SELLERS ===== */}
      <motion.section id="collections" className="collections container" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>
        <h2 className="section-title">{strings.bestSellers}</h2>
        <div className="card-grid fixed-four">
          {[home4, home6, home15, home14].map((img, i) => (
            <motion.div className="jewel-card" key={i} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
              <div className="jewel-img" style={{ backgroundImage: `url(${img})` }} />
              <div className="jewel-info">
                <h3>Exclusive Design</h3>
                <p>Handcrafted perfection</p>
                <button className="btn small outline" onClick={() => setActiveProduct({ id: `bs-${i}`, img, name: "Exclusive Design", desc: "Signature artisanal piece", price: 420000 })}>
                  {strings.quickView}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ===== CATEGORY SECTION ===== */}
      <motion.section id="categories" className="categories container" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.25 }}>
        <h2 className="section-title">{strings.categories}</h2>

        {/* CHIP RAIL (screenshot style) */}
        <div className="category-rail">
          <ul className="category-list" role="tablist" aria-label="Browse by category">
            {Object.keys(categoryData).map((cat) => (
              <li
                key={cat}
                role="tab"
                aria-selected={activeCategory === cat}
                tabIndex={0}
                className={`category-item ${activeCategory === cat ? "active" : ""}`}
                onClick={() => setActiveCategory(cat)}
                onKeyDown={(e) => onCatKeyDown(e, cat)}
              >
                {cat}
              </li>
            ))}
          </ul>
        </div>

        {/* Live search status */}
        {debouncedSearch.trim() && (
          <div className="search-status" aria-live="polite">
            <span className="muted">{strings.resultsFor} </span>
            <b>“{debouncedSearch}”</b> • {filtered.length}
          </div>
        )}

        {/* Live search results */}
        <div className={`card-grid search-grid ${flashSearch ? "flash" : ""}`}>
          {filtered.length === 0 && debouncedSearch.trim() ? (
            <div className="empty">{strings.noResults}</div>
          ) : (
            filtered.map((p) => (
              <div key={p.id} className="product-card">
                <button onClick={() => setActiveProduct(p)} className="product-img-btn"><img src={p.img} alt={p.name} /></button>
                <div className="product-info">
                  <div className="product-name">{p.name}</div>
                  <div className="product-desc">{p.desc}</div>
                  <div className="product-row">
                    <div className="product-price">{price(p)}</div>
                    <div className="product-actions">
                      <button aria-label="Wishlist" onClick={() => addToWish(p)} className="icon-btn lite" title={strings.wishlist}>♡</button>
                      <button onClick={() => addToCart(p, 1)} className="btn primary tiny">{strings.addToCart}</button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.section>

      {/* ===== NEWSLETTER + TESTIMONIAL ===== */}
      <section className="newsletter container">
        <div className="news-left">
          <h3>{strings.newsletterH}</h3>
          <p>{strings.newsletterP}</p>
          <form onSubmit={(e) => { e.preventDefault(); alert("Thank you! You're subscribed."); }} className="news-form">
            <input type="email" required placeholder="you@example.com" />
            <button className="btn primary">{strings.subscribe}</button>
          </form>
        </div>
        <div className="news-right">
          <div className="stars">★★★★★</div>
          <p className="quote">“Exquisite designs and warm service. My engagement ring is perfect!”</p>
          <div className="author">— Nadeesha P.</div>
        </div>
      </section>

      {/* ===== BACK TO TOP ===== */}
      <AnimatePresence>
        {showTop && (
          <motion.button className="to-top" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" title="Back to top">↑</motion.button>
        )}
      </AnimatePresence>
        {/* ===== WHATSAPP FLOAT BUTTON ===== */}
<a
  href="https://wa.me/94761234567" // Change to your actual WhatsApp number (no + or spaces)
  className="whatsapp-float"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Chat on WhatsApp"
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width="28"
    height="28"
    fill="currentColor"
  >
    <path d="M16 .4C7.3.4.2 7.4.2 16.2c0 2.8.7 5.5 2.1 7.9L0 32l8.2-2.2c2.4 1.3 5.1 2 7.8 2 8.8 0 15.8-7.1 15.8-15.8C31.8 7.4 24.8.4 16 .4zm0 28.8c-2.4 0-4.8-.6-6.9-1.8l-.5-.3-4.9 1.3 1.3-4.8-.3-.5c-1.3-2.2-2-4.7-2-7.3 0-7.6 6.2-13.8 13.8-13.8s13.8 6.2 13.8 13.8-6.2 13.8-13.8 13.8zm7.6-10.2c-.4-.2-2.4-1.2-2.8-1.3-.4-.2-.7-.2-1 .2-.3.4-1.1 1.3-1.3 1.6-.2.2-.5.2-.9 0s-1.7-.6-3.2-2c-1.2-1.1-2-2.4-2.2-2.8-.2-.4 0-.6.1-.8s.3-.4.4-.6c.2-.2.3-.4.4-.6.1-.2.1-.4 0-.6-.1-.2-1-2.4-1.3-3.3-.4-.9-.7-.8-1-.8h-.8c-.2 0-.6.1-.9.4-.3.3-1.2 1.1-1.2 2.7s1.2 3.2 1.4 3.5c.2.3 2.4 3.7 5.8 5.1 3.4 1.5 3.4 1 4 1 .6 0 2-0.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.1-.4-.2-.8-.4z"/>
  </svg>
</a>
          

      {/* ===== FOOTER ===== */}
      <footer id="contact" className="footer">
        <div className="container footer-grid">
          <div className="footer-about">
            <h4>Royal Crown Jewels</h4>
            <p>{lang === "TA" ? strings.addressTA : strings.addressEN}</p>
            <p>{lang === "TA" ? strings.estTA : strings.estEN}</p>
          </div>

          <div className="footer-links">
            <a href="#collections">Collections</a>
            <a href="#categories">Categories</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
          </div>

          <div className="footer-contact">
            <a href="tel:+94764322448" className="phone">+94 76 123 4567</a>
            <a href="mailto:info@RoyalCrownJewels.com" className="email">info@RoyalCrownJewels.com</a>
            <div className="socials"><a href="#">Facebook</a><a href="#">Instagram</a><a href="#">YouTube</a></div>
          </div>
        </div>

        <div className="copyright">© 2025 Royal Crown Jewels. Crafted with 💛 in Sri Lanka.</div>
      </footer>

      {/* ===== CATEGORY / CART / WISHLIST MODALS ===== */}
      <AnimatePresence>
        {activeCategory && activeCategory !== "Cart" && activeCategory !== "Wishlist" && (
          <motion.div key="modal-cat" className="category-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content" initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.25 }}>
              <button className="close-btn" onClick={() => setActiveCategory(null)} aria-label="Close">×</button>
              <h2>{activeCategory}</h2>
              <div className="modal-grid">
                {categoryData[activeCategory]?.map((item) => (
                  <motion.div key={item.id} className="modal-card" whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 220, damping: 16 }}>
                    <img src={item.img} alt={item.name} />
                    <h4>{item.name}</h4>
                    <p>{item.desc}</p>
                    <div className="modal-card-row">
                      <span className="price">{price(item)}</span>
                      <div className="modal-actions">
                        <button className="btn small outline" onClick={() => addToWish(item)} title={strings.wishlist}>♡</button>
                        <button className="btn small primary" onClick={() => addToCart(item, 1)}>{strings.addToCart}</button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCategory === "Cart" && (
          <motion.div key="modal-cart" className="category-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content narrow" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 20, opacity: 0 }} transition={{ duration: 0.2 }}>
              <button className="close-btn" onClick={() => setActiveCategory(null)} aria-label="Close">×</button>
              <h2>{strings.cart}</h2>

              {cart.length === 0 ? (
                <div className="empty">{strings.emptyCart}</div>
              ) : (
                <>
                  <div className="list">
                    {cart.map((p) => (
                      <div key={p.id} className="list-item">
                        <img src={p.img} alt={p.name} />
                        <div className="li-meta">
                          <div className="li-name">{p.name}</div>

                          {/* Qty controls */}
                          <div className="qty">
                            <button
                              className="qty-btn"
                              onClick={() => decrementQty(p.id)}
                              disabled={(p.qty || 1) <= 1}
                              aria-label="Decrease quantity"
                            >
                              −
                            </button>
                            <input
                              className="qty-input"
                              type="number"
                              min="1"
                              max="99"
                              value={p.qty || 1}
                              onChange={(e) =>
                                setQty(p.id, Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))
                              }
                              aria-label="Quantity"
                            />
                            <button
                              className="qty-btn"
                              onClick={() => incrementQty(p.id)}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="li-right">
                          <div className="li-price">{fmt(lineTotal(p))}</div>
                          <button className="btn tiny outline" onClick={() => removeFromCart(p.id)}>
                            {strings.remove}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="total-row">
                    <div className="total-label">{strings.total}</div>
                    <div className="total-amt">{fmt(cartTotalLKR)}</div>
                  </div>
                  <button className="btn primary block">Checkout</button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCategory === "Wishlist" && (
          <motion.div key="modal-wish" className="category-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content narrow" initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 20, opacity: 0 }} transition={{ duration: 0.2 }}>
              <button className="close-btn" onClick={() => setActiveCategory(null)} aria-label="Close">×</button>
              <h2>{strings.wishlist}</h2>

              {wishlist.length === 0 ? (
                <div className="empty">{strings.noItems}</div>
              ) : (
                <div className="list">
                  {wishlist.map((p) => (
                    <div key={p.id} className="list-item">
                      <img src={p.img} alt={p.name} />
                      <div className="li-meta"><div className="li-name">{p.name}</div><div className="li-price">{price(p)}</div></div>
                      <div className="li-actions">
                        <button className="btn tiny primary" onClick={() => addToCart(p, 1)}>{strings.addToCart}</button>
                        <button className="btn tiny outline" onClick={() => removeFromWish(p.id)}>{strings.remove}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== QUICK VIEW ===== */}
      <AnimatePresence>
        {activeProduct && (
          <motion.div key="quick" className="category-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="modal-content product" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.98, opacity: 0 }}>
              <button className="close-btn" onClick={() => setActiveProduct(null)} aria-label="Close">×</button>
              <div className="qv-grid">
                <img src={activeProduct.img} alt={activeProduct.name} />
                <div className="qv-meta">
                  <div className="qv-name">{activeProduct.name}</div>
                  <div className="qv-price">{price(activeProduct)}</div>
                  <p className="qv-desc">{activeProduct.desc || "Handcrafted with precision using ethically sourced materials."}</p>

                  {/* Qty row in Quick View */}
                  <div className="qty-row">
                    <span className="qty-label">{strings.qty}</span>
                    <div className="qty">
                      <button
                        className="qty-btn"
                        onClick={() => setSelectedQty((q) => Math.max(1, q - 1))}
                        disabled={selectedQty <= 1}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <input
                        className="qty-input"
                        type="number"
                        min="1"
                        max="99"
                        value={selectedQty}
                        onChange={(e) =>
                          setSelectedQty(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))
                        }
                        aria-label="Quantity"
                      />
                      <button
                        className="qty-btn"
                        onClick={() => setSelectedQty((q) => Math.min(99, q + 1))}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="qv-actions">
                    <button className="btn primary" onClick={() => { addToCart(activeProduct, selectedQty); setActiveProduct(null); setActiveCategory("Cart"); }}>
                      {strings.addToCart}
                    </button>
                    <button className="btn outline" onClick={() => addToWish(activeProduct)}>{strings.wishlist}</button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
